import { APPLICANT_CONTENT } from "@/features/applicant/constants/applicantContent";
import { normalizeGovIdAttachments } from "@/features/applicant/utils/applicationDataHelpers";
import type { ResidentApplicationData } from "@/types/application";

export type GovernmentIdType = "drivers-license" | "passport" | null;

const GOV_ID_MAX_FILE_SIZE_MB = 5;

export function resolveGovernmentIdTypeFromApplication(
  appData: ResidentApplicationData | null | undefined,
): GovernmentIdType {
  if (!appData) return "drivers-license";

  if (appData.goverment_id_type) {
    const govIdType = String(appData.goverment_id_type).toLowerCase();
    if (govIdType.includes("passport")) return "passport";
    if (govIdType.includes("driver")) return "drivers-license";
  }

  const govIdAttachments = normalizeGovIdAttachments(appData.goverment_id_attachment);
  if (govIdAttachments.length > 0 && !appData.goverment_id_type) {
    const fileType = govIdAttachments[0].type || "";
    if (fileType.toLowerCase().includes("passport")) return "passport";
    return "drivers-license";
  }

  const backAttachments = normalizeGovIdAttachments(appData.goverment_id_attachment_back);
  if (backAttachments.length > 0 && !appData.goverment_id_type) {
    return "drivers-license";
  }

  return "drivers-license";
}

/** True when either ID side is already stored, even if the other side is still missing. */
export function hasAnyStoredGovernmentIdFile(
  appData: ResidentApplicationData | null | undefined,
): boolean {
  if (!appData) return false;
  return (
    normalizeGovIdAttachments(appData.goverment_id_attachment).length > 0 ||
    normalizeGovIdAttachments(appData.goverment_id_attachment_back).length > 0
  );
}

/** Prefer the saved application ID type when files are already stored on the server. */
export function resolveGovernmentIdTypeForValidation(input: {
  governmentIdType: GovernmentIdType;
  applicationData: ResidentApplicationData | null | undefined;
}): GovernmentIdType {
  if (!hasAnyStoredGovernmentIdFile(input.applicationData)) {
    return input.governmentIdType;
  }
  return resolveGovernmentIdTypeFromApplication(input.applicationData);
}

export function hasStoredGovernmentIdUploads(
  appData: ResidentApplicationData | null | undefined,
  governmentIdType: GovernmentIdType,
): boolean {
  if (!appData || !governmentIdType) return false;

  const hasFront =
    normalizeGovIdAttachments(appData.goverment_id_attachment).length > 0;
  const hasBack =
    normalizeGovIdAttachments(appData.goverment_id_attachment_back).length > 0;

  if (governmentIdType === "passport") return hasFront;
  return hasFront && hasBack;
}

export type GovernmentIdFieldErrors = {
  governmentIdType?: string;
  governmentIdFiles?: string;
};

function isWithinGovIdSizeLimit(file: File): boolean {
  return file.size / 1024 / 1024 <= GOV_ID_MAX_FILE_SIZE_MB;
}

/**
 * Same rules as the Signature tab on main:
 * - an ID type is required
 * - Driver's License needs a front and a back (a side already saved on the application counts)
 * - Passport needs one image
 * - each new file must be 5 MB or less
 *
 * A saved passport does not count as a license, and a saved license does not count as a passport.
 */
export function getGovernmentIdFieldErrors(input: {
  governmentIdType: GovernmentIdType;
  applicationData: ResidentApplicationData | null | undefined;
  driversLicenseFront: File | null;
  driversLicenseBack: File | null;
  passportImage: File | null;
}): GovernmentIdFieldErrors {
  const errors: GovernmentIdFieldErrors = {};
  const { governmentIdType, applicationData } = input;

  if (!governmentIdType) {
    errors.governmentIdType = APPLICANT_CONTENT.errors.signature.governmentIdType;
    return errors;
  }

  const savedType = String(applicationData?.goverment_id_type || "").toLowerCase();
  const savedIsPassport = savedType.includes("passport");
  const savedIsDriversLicense =
    savedType.includes("driver") || savedType.includes("license");
  const storedFront =
    normalizeGovIdAttachments(applicationData?.goverment_id_attachment).length > 0;
  const storedBack =
    normalizeGovIdAttachments(applicationData?.goverment_id_attachment_back).length > 0;

  if (governmentIdType === "drivers-license") {
    const hasFront =
      Boolean(input.driversLicenseFront) || (storedFront && !savedIsPassport);
    const hasBack = Boolean(input.driversLicenseBack) || storedBack;

    if (!hasFront && !hasBack) {
      errors.governmentIdFiles = "Upload the front and back of your driver's license.";
      return errors;
    }
    if (!hasFront) {
      errors.governmentIdFiles = "Upload the front of your driver's license.";
      return errors;
    }
    if (!hasBack) {
      errors.governmentIdFiles = "Upload the back of your driver's license.";
      return errors;
    }
    if (input.driversLicenseFront && !isWithinGovIdSizeLimit(input.driversLicenseFront)) {
      errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.driversLicenseFrontSize;
    } else if (
      input.driversLicenseBack &&
      !isWithinGovIdSizeLimit(input.driversLicenseBack)
    ) {
      errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.driversLicenseBackSize;
    }
    return errors;
  }

  const hasPassport =
    Boolean(input.passportImage) || (storedFront && !savedIsDriversLicense);
  if (!hasPassport) {
    errors.governmentIdFiles = "Upload your passport.";
    return errors;
  }
  if (input.passportImage && !isWithinGovIdSizeLimit(input.passportImage)) {
    errors.governmentIdFiles = APPLICANT_CONTENT.errors.signature.passportImageSize;
  }

  return errors;
}

export function getGovernmentIdValidationErrors(input: {
  governmentIdType: GovernmentIdType;
  applicationData: ResidentApplicationData | null | undefined;
  driversLicenseFront: File | null;
  driversLicenseBack: File | null;
  passportImage: File | null;
}): string[] {
  const fieldErrors = getGovernmentIdFieldErrors(input);
  const errors: string[] = [];
  if (fieldErrors.governmentIdType) errors.push(fieldErrors.governmentIdType);
  if (fieldErrors.governmentIdFiles) errors.push(fieldErrors.governmentIdFiles);
  return errors;
}

export function isGovernmentIdUploadComplete(input: {
  governmentIdType: GovernmentIdType;
  applicationData: ResidentApplicationData | null | undefined;
  driversLicenseFront: File | null;
  driversLicenseBack: File | null;
  passportImage: File | null;
}): boolean {
  return (
    getGovernmentIdValidationErrors(input).length === 0
  );
}
